import type { z } from "zod";
import {
  PACKAGE_CATEGORIES,
  PACKAGE_ROUTES,
  packageListResponseSchema,
  packageListSchema,
  packageResponseSchema,
  type Package,
} from "@nh/shared";
import { apiFetch } from "@/shared/api/client";

/**
 * La página de paquetes, tal y como la define el contrato.
 *
 * Se infiere del esquema en vez de escribirse a mano —y en vez de tocar
 * `@nh/shared`, que no es de esta pantalla— para que el día que el
 * contrato añada un campo, este tipo lo tenga sin que nadie se acuerde.
 */
export type PackageList = z.infer<typeof packageListSchema>;

export const packagesQueryKey = ["packages", "public"] as const;

/**
 * Cuántos paquetes se piden de una vez.
 *
 * Un catálogo de paquetes es de decenas, no de miles: pedirlo entero
 * permite repartirlo por categoría en el navegador y que cambiar de
 * pestaña sea instantáneo en vez de un viaje a la red que puede fallar.
 * `packageListQuerySchema` topa este valor en 100, así que 100 es el
 * máximo que el contrato acepta.
 */
export const PACKAGES_PAGE_SIZE = 100;

/**
 * GET /api/packages — los paquetes PÚBLICOS (solo `published`).
 *
 * ── Se piden las dos categorías, en DOS peticiones ───────────────────
 * El servidor, SIN `?category=`, devuelve solo los personales (regla de
 * negocio: "los paquetes de franquiciatario NO aparecen en la sección de
 * paquetes personales"). Esta función creía lo contrario y pedía una
 * sola vez, así que la pestaña de negocio digital SIEMPRE salía vacía
 * (visto el 28-ago con los 5 mayoristas sembrados). Ahora se piden las
 * dos categorías a la vez y se unen aquí; el reparto por pestaña sigue
 * en `packagesOfCategory`, que es puro.
 *
 * `PACKAGE_ROUTES.publicList` ya trae el prefijo `/api`, de ahí el
 * `baseUrl: ""`: si no, `apiFetch` lo antepondría otra vez y la petición
 * saldría a `/api/api/packages`.
 */
export async function fetchPackages(): Promise<PackageList> {
  const [personal, franchisee] = await Promise.all(
    PACKAGE_CATEGORIES.map((category) =>
      apiFetch(
        `${PACKAGE_ROUTES.publicList}?category=${category}&perPage=${PACKAGES_PAGE_SIZE}`,
        packageListResponseSchema,
        { baseUrl: "" },
      ),
    ),
  );
  // Sin duplicados por id: un servidor (o un stub) que ignore el filtro
  // devolvería lo mismo dos veces.
  const seen = new Set<string>();
  const items = [...(personal?.items ?? []), ...(franchisee?.items ?? [])].filter((pkg) => {
    if (seen.has(pkg.id)) return false;
    seen.add(pkg.id);
    return true;
  });
  return { items, total: items.length, page: 1, perPage: PACKAGES_PAGE_SIZE };
}

/**
 * UN paquete por su slug. Lo pide `/franquicias/adquirir` cuando llega
 * con `?paquete=…`: la pantalla necesita el objeto fresco —precio,
 * imagen, descuento de franquiciatario— y no lo que hubiera guardado.
 */
export function fetchPackageBySlug(slug: string): Promise<Package> {
  return apiFetch(PACKAGE_ROUTES.publicBySlug(slug), packageResponseSchema, {
    baseUrl: "",
  }).then((data) => data.package);
}
